"use client";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Link from "next/link";
import { formatDistanceToNow } from "date-fns";
import { StaticImport } from "next/dist/shared/lib/get-img-props";
import { notFound, useParams } from "next/navigation";
import RelatedBlogs from "@/components/website/BlogDetail/RelatedBlogs";

interface Blog {
  _id: string;
  title: string;
  description: string;
  slug: string;
  banner: StaticImport;
  createdAt: string;
  updatedAt: string;
  blogId: string;
  authorName: string;
  authorImage: string;
}

const BlogSkeleton = () => {
  return (
    <div className="w-11/12 lg:w-9/12 3xl:w-8/12 mx-auto py-[5rem] space-y-8 animate-pulse">
      {/* Back Button */}
      <div className="inline-flex items-center text-zinc-500 gap-2">
        <div className="w-5 h-5 bg-zinc-300 rounded-full" />
        <div className="h-4 w-16 bg-zinc-300 rounded" />
      </div>

      {/* Title */}
      <div className="h-10 md:h-14 w-3/4 bg-zinc-300 rounded" />

      {/* Author & Date */}
      <div className="flex flex-wrap gap-4 items-center text-zinc-500 text-sm md:text-base">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-zinc-300" />
          <div className="h-4 w-24 bg-zinc-300 rounded" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-zinc-300 rounded-full" />
          <div className="h-4 w-20 bg-zinc-300 rounded" />
        </div>
      </div>

      {/* Banner Image */}
      <div className="w-full h-[60vh] bg-zinc-200 rounded-lg" />

      {/* Blog Content */}
      <div className="space-y-4">
        <div className="h-4 w-full bg-zinc-200 rounded" />
        <div className="h-4 w-11/12 bg-zinc-200 rounded" />
        <div className="h-4 w-10/12 bg-zinc-200 rounded" />
        <div className="h-4 w-full bg-zinc-200 rounded" />
        <div className="h-4 w-9/12 bg-zinc-200 rounded" />
        <div className="h-4 w-4/6 bg-zinc-200 rounded" />
      </div>
    </div>
  );
};

export default function BlogDetailMain() {
  const params = useParams();
  const id = params.id as string;
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/blogs/${id}`,
          {
            next: { revalidate: 20 },
          }
        );

        if (!res.ok) {
          throw new Error("Blog not found");
        }

        const response = await res.json();
        setBlog(response.data);
      } catch (err) {
      
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlog();
    }
  }, [id]);

  if (error) {
    return notFound();
  }

  if (loading || !blog) {
    return (
      <>
        <BlogSkeleton />
        <div className="w-11/12 md:w-10/12 mx-auto">
          <RelatedBlogs id={""} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="w-11/12 lg:w-9/12 3xl:w-8/12 mx-auto py-[5rem] space-y-8">
        {/* Back Button */}
        <Link
          href="/blogs"
          className="inline-flex items-center text-zinc-700 hover:text-zinc-800 transition duration-300 hover:scale-105"
        >
          <Icon icon="ic:outline-arrow-left" className="w-5 h-5 mr-2" />
          <span className="font-medium text-base">Back</span>
        </Link>

        {/* Title */}
        <h1 className="text-3xl md:text-5xl font-bold tracking-wide text-black">
          {blog.title}
        </h1>

        {/* Author & Date */}
        <div className="flex flex-wrap gap-4 items-center text-sm md:text-base text-zinc-600 font-medium">
          <div className="flex items-center gap-2">
            <Image
              src={blog?.authorImage || "/Guides/Guide.jpg"}
              alt={blog?.authorName || "Prem Gurung"}
              width={32}
              height={32}
              className="w-8 h-8 rounded-full object-cover overflow-hidden"
            />
            By<span>{blog?.authorName || "Prem Gurung"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon icon="uil:calender" className="w-4 h-4" />
            <span>
              {formatDistanceToNow(new Date(blog.createdAt), {
                addSuffix: true,
              })}
            </span>
          </div>
        </div>

        {/* Banner Image */}
        <div className="w-full">
          <Image
            width={1000}
            height={1000}
            src={blog.banner}
            alt={blog.slug}
            className="w-full max-h-[70vh] rounded-lg object-cover object-top"
            priority
          />
        </div>

        {/* Blog Content */}
        <div
          className="ql-editor text-base  leading-relaxed"
          dangerouslySetInnerHTML={{ __html: blog.description }}
        />
      </div>

      <div className="w-11/12 md:w-10/12 mx-auto">
        <RelatedBlogs id={blog.blogId} />
      </div>
    </>
  );
}
